import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import SkuBarcodeModal, { normalizeSkuForBarcode } from '../components/SkuBarcodeModal';
import { getAuthHeaders, getToken } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { getOrderAssignment } from '../utils/orderMetrics';
import './HoldItemsPage.css';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/60';
const PRODUCT_META_CACHE_KEY = 'productMetaBySku';
const PRODUCT_META_CACHE_TTL = 4 * 60 * 60 * 1000;
const PRODUCT_META_CACHE_LIMIT = 500;
const WAREHOUSE_LOC_KEY = 'warehouseLocBySku';
const HOLD_FOUND_KEY = 'holdFoundLocal';
const productMetaRequests = new Map();

const collator = typeof Intl !== 'undefined'
  ? new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
  : null;

const compareText = (a = '', b = '') => {
  const left = String(a);
  const right = String(b);
  if (collator) return collator.compare(left, right);
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
};

const normalizeUser = (value) => (value ? String(value).trim().toLowerCase() : '');

const normStatus = (status) => (typeof status === 'string' ? status.toLowerCase() : status);
const isHoldStatus = (status) => {
  const s = normStatus(status);
  return s === 'hold' || s === 'on_hold';
};

const emptyProductMeta = () => ({
  image: PLACEHOLDER_IMAGE,
  location: '',
  locationCashCarry: '',
  zone: '',
  lane: '',
  barcode: '',
  hasImage: false,
});

const readProductMetaCache = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PRODUCT_META_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const fresh = {};
    Object.entries(parsed || {}).forEach(([sku, entry]) => {
      if (entry?.savedAt && now - entry.savedAt < PRODUCT_META_CACHE_TTL && entry.meta) {
        fresh[sku] = entry;
      }
    });
    return fresh;
  } catch {
    return {};
  }
};

const writeProductMetaCache = (cache) => {
  if (typeof window === 'undefined') return;
  try {
    const entries = Object.entries(cache || {})
      .sort((a, b) => Number(b[1]?.savedAt || 0) - Number(a[1]?.savedAt || 0))
      .slice(0, PRODUCT_META_CACHE_LIMIT);
    window.localStorage.setItem(PRODUCT_META_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch (err) {
    console.warn('Failed to persist product meta cache:', err);
  }
};

const getCachedProductMeta = (sku) => {
  const key = String(sku || '').trim();
  if (!key) return null;
  return readProductMetaCache()[key]?.meta || null;
};

const setCachedProductMeta = (sku, meta) => {
  const key = String(sku || '').trim();
  if (!key || !meta) return;
  const cache = readProductMetaCache();
  cache[key] = { savedAt: Date.now(), meta };
  writeProductMetaCache(cache);
};

const extractBaseSku = (value) => {
  if (!value) return '';
  const str = String(value).trim();
  const idx = str.indexOf('-');
  if (idx <= 0) return str;
  const suffix = str.slice(idx + 1);
  if (/^\d+$/.test(suffix)) return str;
  return str.slice(0, idx);
};

const getSkuLookupCandidates = (value) => {
  if (!value) return [];
  const sku = String(value).trim();
  if (!sku) return [];
  const idx = sku.indexOf('-');
  if (idx <= 0) return [sku];
  const suffix = sku.slice(idx + 1);
  const baseSku = sku.slice(0, idx);
  if (/^c$/i.test(suffix)) return [sku, baseSku];
  const baseCandidate = extractBaseSku(sku);
  return baseCandidate && baseCandidate !== sku ? [baseCandidate, sku] : [sku];
};

const toCatalogUrl = (filePath) => {
  if (!filePath) return null;
  const raw = String(filePath).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  return `/media/catalog/product${normalized}`;
};

const pickPrimaryMediaEntry = (entries) => {
  if (!Array.isArray(entries)) return null;
  const preferredTypes = new Set(['image', 'small_image', 'thumbnail']);
  const prioritized = entries.find((entry) => {
    const types = Array.isArray(entry?.types) ? entry.types : [];
    return types.some((type) => preferredTypes.has(String(type).toLowerCase()));
  });
  return prioritized || entries[0] || null;
};

const fetchProductMeta = async (sku, fallbackSku) => {
  const candidates = getSkuLookupCandidates(sku);
  if (fallbackSku && !candidates.includes(fallbackSku)) candidates.push(fallbackSku);
  const requestKey = candidates.join('|');
  if (productMetaRequests.has(requestKey)) return productMetaRequests.get(requestKey);

  const request = (async () => {
    try {
      let fallbackMeta = null;
      for (const candidate of candidates) {
        try {
          const cached = getCachedProductMeta(candidate);
          if (cached) {
            if (cached.hasImage) return cached;
            if (!fallbackMeta) fallbackMeta = cached;
            continue;
          }

          const response = await apiFetch(`/rest/V1/products/${encodeURIComponent(candidate)}`, {
            headers: getAuthHeaders(),
          });
          if (!response.ok) continue;
          const data = await response.json();
          const galleryEntry = pickPrimaryMediaEntry(data?.media_gallery_entries);

          const customAttrs = Array.isArray(data?.custom_attributes) ? data.custom_attributes : [];
          const attrMap = customAttrs.reduce((acc, attr) => {
            if (attr?.attribute_code) acc[attr.attribute_code] = attr.value;
            return acc;
          }, {});

          const attrImagePath = attrMap.image || attrMap.small_image || attrMap.thumbnail;
          const resolvedImage = toCatalogUrl(attrImagePath || galleryEntry?.file);
          const image = resolvedImage || PLACEHOLDER_IMAGE;

          const meta = {
            image,
            location: attrMap.location || '',
            locationCashCarry: attrMap.location_cashandcarry || '',
            zone: attrMap.zone || '',
            lane: attrMap.lane || '',
            barcode: data?.barcode || attrMap.barcode || attrMap.ean || attrMap.gtin || attrMap.upc || '',
            hasImage: Boolean(resolvedImage),
          };
          setCachedProductMeta(candidate, meta);
          if (resolvedImage) return meta;
          if (!fallbackMeta) fallbackMeta = meta;
        } catch (inner) {
          // try next candidate
        }
      }
      return fallbackMeta || emptyProductMeta();
    } catch (error) {
      console.warn('Product meta lookup failed for', sku, error);
      return emptyProductMeta();
    }
  })();

  productMetaRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    productMetaRequests.delete(requestKey);
  }
};

const applyProductMetaToItem = (item, meta = emptyProductMeta()) => ({
  ...item,
  image_url: meta.image || item.image_url || PLACEHOLDER_IMAGE,
  extension_attributes: {
    ...item.extension_attributes,
    location: item.extension_attributes?.location || meta.location || '',
    zone: item.extension_attributes?.zone || meta.zone || '',
    lane: item.extension_attributes?.lane || meta.lane || '',
    barcode: item.extension_attributes?.barcode || meta.barcode || '',
  },
});

const readPickedSelections = () => {
  try {
    const raw = localStorage.getItem('pickedSelections');
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const readWarehouseLocations = () => {
  try {
    const raw = localStorage.getItem(WAREHOUSE_LOC_KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const readFoundLocal = () => {
  try {
    const raw = localStorage.getItem(HOLD_FOUND_KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const HoldItemsPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pickedSelections, setPickedSelections] = useState(() => readPickedSelections());
  const [committedLoc, setCommittedLoc] = useState(() => readWarehouseLocations());
  const [locInputs, setLocInputs] = useState({});
  const [foundLocal, setFoundLocal] = useState(() => readFoundLocal());
  const [skuCodeValue, setSkuCodeValue] = useState('');
  const skuHoldTimerRef = useRef(null);

  const storedUsername = localStorage.getItem('operator') || localStorage.getItem('username') || '';
  const usernameKey = normalizeUser(storedUsername);

  // Keep in sync if another tab/page changes selections.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event && event.key && event.key !== 'pickedSelections') return;
      setPickedSelections(readPickedSelections());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => () => {
    if (skuHoldTimerRef.current) clearTimeout(skuHoldTimerRef.current);
  }, []);

  const clearSkuHoldTimer = () => {
    if (skuHoldTimerRef.current) {
      clearTimeout(skuHoldTimerRef.current);
      skuHoldTimerRef.current = null;
    }
  };

  const startSkuHold = (sku) => {
    clearSkuHoldTimer();
    const value = normalizeSkuForBarcode(sku);
    if (!value) return;
    skuHoldTimerRef.current = setTimeout(() => {
      setSkuCodeValue(value);
      skuHoldTimerRef.current = null;
    }, 550);
  };

  useEffect(() => {
    let cancelled = false;

    const enrichOrdersWithProductMeta = async (ordersToEnrich) => {
      const enriched = await Promise.all((ordersToEnrich || []).map(async (detail) => {
        const itemsById = (detail.items || []).reduce((acc, it) => {
          acc[it.item_id] = it;
          return acc;
        }, {});

        const itemsWithMeta = await Promise.all((detail.items || []).map(async (item) => {
          const parent = item.parent_item_id ? itemsById[item.parent_item_id] : null;
          const meta = await fetchProductMeta(item.sku, parent?.sku);
          return applyProductMetaToItem(item, meta);
        }));

        return { ...detail, items: itemsWithMeta };
      }));

      if (!cancelled) setOrders(enriched);
    };

    const fetchOrders = async () => {
      try {
        await getToken();
        const response = await apiFetch(
          `/rest/V1/orders?searchCriteria[filter_groups][0][filters][0][field]=status&searchCriteria[filter_groups][0][filters][0][value]=picking&searchCriteria[filter_groups][0][filters][0][condition_type]=eq`,
          { method: 'GET', headers: getAuthHeaders() }
        );
        const data = await response.json();

        const detailed = await Promise.all((data.items || []).map(async (order) => {
          const detailRes = await apiFetch(`/rest/V1/orders/${order.entity_id}`, {
            headers: getAuthHeaders(),
          });
          const detail = await detailRes.json();

          const assignedTo = getOrderAssignment(detail).currentOperator;
          const assignedKey = normalizeUser(assignedTo);
          if (assignedKey && usernameKey && assignedKey !== usernameKey) {
            return null;
          }

          const items = (detail.items || []).map((item) => ({
            ...item,
            image_url: item.image_url || PLACEHOLDER_IMAGE,
          }));
          return { ...detail, items };
        }));

        if (cancelled) return;
        const filtered = detailed.filter(Boolean);
        setOrders(filtered);
        setPickedSelections(readPickedSelections());
        setLoading(false);
        enrichOrdersWithProductMeta(filtered).catch((err) => {
          console.warn('Hold items product metadata enrichment failed:', err);
        });
      } catch (e) {
        console.error('Failed to load hold items:', e);
        if (!cancelled) {
          setError(e.message || 'Failed to load orders');
          setLoading(false);
        }
      }
    };

    fetchOrders();
    return () => { cancelled = true; };
  }, [usernameKey]);

  const pickedMapForOrder = (order) => {
    const candidates = [String(order.increment_id), String(order.entity_id)].filter(Boolean);
    for (const key of candidates) {
      if (pickedSelections[key]) return pickedSelections[key];
    }
    return {};
  };

  // Persist a found/removed decision back to the original order (same store the
  // picking screen reads), so the item reflects there and drops off this list.
  const setItemStatus = useCallback((incrementId, itemId, status) => {
    const store = readPickedSelections();
    const orderKey = String(incrementId);
    const itemKey = String(itemId);
    const orderMap = { ...(store[orderKey] || {}) };
    orderMap[itemKey] = status;
    const next = { ...store, [orderKey]: orderMap };
    try {
      localStorage.setItem('pickedSelections', JSON.stringify(next));
    } catch (err) {
      console.warn('Failed to persist picked selections:', err);
    }
    setPickedSelections(next);
  }, []);

  // "Found" here is a local, on-hold-only mark: it does NOT touch the original
  // order. The item stays visible but sinks to the bottom of the list.
  const toggleFoundLocal = useCallback((rowKey) => {
    setFoundLocal((prev) => {
      const next = { ...prev };
      if (next[rowKey]) delete next[rowKey];
      else next[rowKey] = true;
      try {
        localStorage.setItem(HOLD_FOUND_KEY, JSON.stringify(next));
      } catch (err) {
        console.warn('Failed to persist local found marks:', err);
      }
      return next;
    });
  }, []);

  const persistWarehouseLocations = (map) => {
    try {
      localStorage.setItem(WAREHOUSE_LOC_KEY, JSON.stringify(map));
    } catch (err) {
      console.warn('Failed to persist warehouse locations:', err);
    }
  };

  const commitLocation = useCallback((sku, rawValue) => {
    if (!sku) return;
    const value = String(rawValue || '').trim().toUpperCase();
    setCommittedLoc((prev) => {
      const next = { ...prev };
      if (value) next[sku] = value;
      else delete next[sku];
      persistWarehouseLocations(next);
      return next;
    });
  }, []);

  // Group on-hold items by product (SKU), like Multi-order picking: the same
  // item appearing in several orders becomes one card listing each order.
  const holdGroups = useMemo(() => {
    const groups = new Map();
    let orderCount = 0;
    orders.forEach((order) => {
      const map = pickedMapForOrder(order);
      (order.items || []).forEach((item) => {
        const entry = map[String(item.item_id)];
        const status = typeof entry === 'string' ? entry : entry?.status;
        if (!isHoldStatus(status)) return;

        const sku = item.sku || '';
        const groupKey = sku || `item-${item.item_id}`;
        const existing = groups.get(groupKey) || {
          key: groupKey,
          sku,
          name: item.name || 'Item',
          image: item.image_url || PLACEHOLDER_IMAGE,
          barcode: item.extension_attributes?.barcode || '',
          lane: item.extension_attributes?.lane || '',
          unit: item.extension_attributes?.unit || 'pcs',
          entries: [],
          totalQty: 0,
        };

        if ((!existing.image || existing.image === PLACEHOLDER_IMAGE)
          && item.image_url && item.image_url !== PLACEHOLDER_IMAGE) {
          existing.image = item.image_url;
        }
        if (!existing.barcode && item.extension_attributes?.barcode) {
          existing.barcode = item.extension_attributes.barcode;
        }
        if (!existing.lane && item.extension_attributes?.lane) {
          existing.lane = item.extension_attributes.lane;
        }

        const qty = Number(item.qty_ordered) || 0;
        existing.entries.push({
          entryKey: `${order.increment_id}-${item.item_id}`,
          incrementId: order.increment_id,
          itemId: item.item_id,
          qty,
        });
        existing.totalQty += qty;
        groups.set(groupKey, existing);
      });
    });
    orderCount = orders.length;
    return { list: Array.from(groups.values()), orderCount };
  }, [orders, pickedSelections]);

  const sortedGroups = useMemo(() => {
    const rows = [...holdGroups.list];
    rows.sort((a, b) => {
      // Groups marked Found (on-hold-only) sink to the bottom.
      const fa = foundLocal[a.key] ? 1 : 0;
      const fb = foundLocal[b.key] ? 1 : 0;
      if (fa !== fb) return fa - fb;

      const la = committedLoc[a.sku] || '';
      const lb = committedLoc[b.sku] || '';
      if (la && !lb) return -1;
      if (!la && lb) return 1;
      if (la && lb) {
        const diff = compareText(la, lb);
        if (diff !== 0) return diff;
      }
      return compareText(a.name, b.name);
    });
    return rows;
  }, [holdGroups, committedLoc, foundLocal]);

  const locatedCount = sortedGroups.filter((group) => committedLoc[group.sku]).length;
  const foundCount = sortedGroups.filter((group) => foundLocal[group.key]).length;

  const getInputValue = (group) => {
    if (Object.prototype.hasOwnProperty.call(locInputs, group.sku)) return locInputs[group.sku];
    return committedLoc[group.sku] || '';
  };

  const handleLocChange = (sku, value) => {
    setLocInputs((prev) => ({ ...prev, [sku]: value }));
  };

  const handleLocCommit = (sku) => {
    const value = Object.prototype.hasOwnProperty.call(locInputs, sku)
      ? locInputs[sku]
      : (committedLoc[sku] || '');
    commitLocation(sku, value);
  };

  const handleLocKeyDown = (event, sku) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      handleLocCommit(sku);
      if (event.key === 'Enter') event.currentTarget.blur();
    }
  };

  return (
    <div className="hold-page">
      <SkuBarcodeModal sku={skuCodeValue} onClose={() => setSkuCodeValue('')} />
      <div className="hold-shell">
        <header className="hold-header">
          <div className="hold-actions">
            <button className="hold-back" onClick={() => navigate('/order-list')}>
              ← Order List
            </button>
            <button className="hold-back alt" onClick={() => navigate('/picking')}>
              Picking
            </button>
          </div>
          <div className="hold-headline">
            <h1>On Hold</h1>
            <p>
              {sortedGroups.length} product{sortedGroups.length === 1 ? '' : 's'} on hold across {holdGroups.orderCount}
              {' '}order{holdGroups.orderCount === 1 ? '' : 's'}
              {sortedGroups.length > 0 ? ` · ${locatedCount} located · ${foundCount} found` : ''}. Scan/search the
              warehouse location, type it in and press Enter to sort the list.
            </p>
          </div>
        </header>

        <LoadingOverlay show={loading} ariaLabel="Loading on-hold items" />
        {error && !loading && <div className="hold-state hold-state--error">{error}</div>}

        {!loading && !error && sortedGroups.length === 0 && (
          <div className="hold-state hold-state--empty">
            <h3>No items on hold</h3>
            <p>Items marked as On Hold during picking will show up here, grouped for the warehouse run.</p>
          </div>
        )}

        {!loading && !error && sortedGroups.length > 0 && (
          <div className="hold-list">
            {sortedGroups.map((group) => {
              const isFoundLocal = Boolean(foundLocal[group.key]);
              const multiOrder = group.entries.length > 1;
              return (
              <div className={`hold-item${isFoundLocal ? ' is-found' : ''}`} key={group.key}>
                <div className="hold-loc">
                  <input
                    type="text"
                    className="hold-loc-input"
                    value={getInputValue(group)}
                    onChange={(e) => handleLocChange(group.sku, e.target.value)}
                    onBlur={() => handleLocCommit(group.sku)}
                    onKeyDown={(e) => handleLocKeyDown(e, group.sku)}
                    placeholder="Location"
                    aria-label={`Warehouse location for ${group.name}`}
                    spellCheck={false}
                    autoComplete="off"
                  />
                </div>

                <img src={group.image} alt={group.name} className="hold-item-img" />

                <div className="hold-details">
                  <div className="hold-name-row">
                    <p className="hold-name"><strong>{group.name}</strong></p>
                    <span className="hold-chip--order">{group.totalQty} {group.unit} total</span>
                    {multiOrder && (
                      <span className="hold-chip--multi">{group.entries.length} orders</span>
                    )}
                  </div>
                  <p className="hold-store">Lane: <span>{group.lane || '—'}</span></p>
                  {group.barcode && (
                    <p className="hold-barcode">Barcode: <span>{group.barcode}</span></p>
                  )}
                  {group.sku && (
                    <p className="hold-sku">
                      SKU:{' '}
                      <button
                        type="button"
                        className="sku-hold-button"
                        onPointerDown={() => startSkuHold(group.sku)}
                        onPointerUp={clearSkuHoldTimer}
                        onPointerCancel={clearSkuHoldTimer}
                        onPointerLeave={clearSkuHoldTimer}
                        onContextMenu={(event) => event.preventDefault()}
                        title="Hold to show scan code"
                      >
                        {group.sku}
                      </button>
                    </p>
                  )}

                  <ul className="hold-orders">
                    {group.entries.map((entry) => (
                      <li className="hold-order-entry" key={entry.entryKey}>
                        <span className="hold-chip--order">#{entry.incrementId}</span>
                        <span className="hold-order-qty">{entry.qty} {group.unit}</span>
                        <button
                          type="button"
                          className="hold-remove-link"
                          onClick={() => setItemStatus(entry.incrementId, entry.itemId, 'removed')}
                          title={`Remove from order #${entry.incrementId}`}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="hold-item-actions">
                  <button
                    className={`picking-button action-button hold-found${isFoundLocal ? ' found-active' : ''}`}
                    onClick={() => toggleFoundLocal(group.key)}
                  >
                    {isFoundLocal ? 'Found ✓' : 'Found'}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HoldItemsPage;
